import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Heart } from 'lucide-react';
import AboutBookModal from './AboutBookModal';
import { BACKEND_URL } from '@/config/api';

/**
 * PersonalizedDropdown - Shows book recommendations based on user's onboarding preferences
 * 
 * Fetches books that match the user's selected book_type (Fiction/Non-Fiction) and genres
 * from their onboarding responses. Uses expandable grid instead of carousel.
 */
const PersonalizedDropdown = ({ onAddToLibrary, onReadNow, isExpanded = false, onToggleExpand }) => {
  const [selectedBook, setSelectedBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPreferences, setHasPreferences] = useState(false);
  const [preferenceInfo, setPreferenceInfo] = useState({ book_type: null, genres: [] });

  // Fetch personalized recommendations
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/recommendations/personalized?limit=20`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setBooks(data.recommendations || []);
          setHasPreferences(data.has_preferences || false);
          setPreferenceInfo({
            book_type: data.book_type,
            genres: data.preferred_genres || []
          });
        }
      } catch (error) {
        console.error('[PersonalizedDropdown] Error fetching recommendations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, []);

  const [booksPerRow, setBooksPerRow] = useState(8);

  // Calculate books per row based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setBooksPerRow(4);
      } else if (window.innerWidth < 1024) {
        setBooksPerRow(6);
      } else {
        setBooksPerRow(8);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  // Format the preference info for display
  const getPreferenceLabel = () => {
    const parts = [];
    if (preferenceInfo.book_type) {
      parts.push(preferenceInfo.book_type === 'fiction' ? 'Fiction' : 'Non-Fiction');
    }
    if (preferenceInfo.genres && preferenceInfo.genres.length > 0) {
      const genreCount = preferenceInfo.genres.length;
      if (genreCount <= 2) {
        parts.push(preferenceInfo.genres.map(g => 
          g.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        ).join(', '));
      } else {
        parts.push(`${genreCount} genres`);
      }
    }
    return parts.join(' • ');
  };

  const displayedBooks = isExpanded ? books : books.slice(0, booksPerRow);
  const hasMore = books.length > booksPerRow;
  const remainingCount = books.length - booksPerRow;

  // Don't render if loading
  if (loading) {
    return (
      <section className="rounded-2xl bg-gradient-to-br from-purple-900/40 to-indigo-900/40 p-4 shadow-xl shadow-slate-950/50 ring-1 ring-purple-500/30">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-purple-400" />
          <h2 className="text-base font-semibold sm:text-lg">Based On Your Preferences</h2>
        </div>
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
          {[...Array(booksPerRow)].map((_, i) => (
            <div key={i}>
              <div className="aspect-[2/3] w-full animate-pulse rounded-lg bg-purple-800/30 mb-2" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-purple-800/30 mb-1" />
              <div className="h-2 w-1/2 animate-pulse rounded bg-purple-800/30" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Don't render if user has no preferences or no matching books
  if (!hasPreferences || books.length === 0) {
    return null;
  }

  return (
    <>
      <section className="rounded-2xl bg-gradient-to-br from-purple-900/40 to-indigo-900/40 p-4 shadow-xl shadow-slate-950/50 ring-1 ring-purple-500/30">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-purple-400" />
            <div>
              <h2 className="text-base font-semibold sm:text-lg">Based On Your Preferences</h2>
              {preferenceInfo.book_type && (
                <p className="text-xs text-purple-300/70">{getPreferenceLabel()}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-xs text-purple-300/70">
              {books.length} {books.length === 1 ? 'match' : 'matches'}
            </span>
            
            {hasMore && (
              <button
                onClick={toggleExpand}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-800/50 hover:bg-purple-700/50 text-purple-200 text-sm font-medium transition"
                data-testid="personalized-expand-btn"
              >
                {isExpanded ? (
                  <>
                    <span>Show Less</span>
                    <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <span>+{remainingCount} More</span>
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Books Grid */}
        <div className="mt-4">
          <div 
            className={`grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3 transition-all duration-300 ${
              isExpanded ? 'max-h-none' : 'max-h-[280px] overflow-hidden'
            }`}
          >
            {displayedBooks.map((book) => (
              <div
                key={book.id}
                onClick={() => setSelectedBook(book)}
                className="group cursor-pointer"
              >
                <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-slate-800 mb-1.5 ring-1 ring-purple-500/30 transition group-hover:ring-purple-400 group-hover:ring-2">
                  {book.cover_url ? (
                    <img
                      src={`${BACKEND_URL}${book.cover_url}`}
                      alt={book.title}
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-500">
                      <BookOpen className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <h3 className="font-medium text-slate-50 text-[10px] sm:text-xs line-clamp-2 leading-tight mb-0.5">
                  {book.title}
                </h3>
                <p className="text-[9px] sm:text-[10px] text-slate-400 line-clamp-1">
                  {book.author}
                </p>
              </div>
            ))}
          </div>
          
          {/* Gradient fade when collapsed */}
          {!isExpanded && hasMore && (
            <div className="relative">
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-purple-900/80 to-transparent pointer-events-none" />
              <button
                onClick={toggleExpand}
                className="relative z-10 w-full mt-2 py-2 text-center text-sm text-purple-300 hover:text-purple-200 font-medium transition"
              >
                +{remainingCount} more recommendations
              </button>
            </div>
          )}
        </div>
      </section>

      {selectedBook && (
        <AboutBookModal
          book={selectedBook}
          isOpen={!!selectedBook}
          onClose={() => setSelectedBook(null)}
          isAuthenticated={true}
          onReadNow={onReadNow}
          onAddToLibrary={onAddToLibrary}
        />
      )}
    </>
  );
};

export default PersonalizedDropdown;
